/** Safe startup timings. No resource names, URLs, account IDs or map data. */
export type StartupPhase = 'initial_map' | 'account' | 'application';
export type StartupOutcome = 'completed' | 'failed';

interface StartupVisibilityProps {
  visibility_state: string;
  visibility_observed_hidden: string;
  visibility_observation_start_ms?: string;
}

interface NavigationTimings {
  domainLookupStart: number;
  domainLookupEnd: number;
  connectStart: number;
  connectEnd: number;
  requestStart: number;
  responseStart: number;
  responseEnd: number;
  domInteractive: number;
}

interface StartupTimingEnvironment {
  now: () => number;
  visibility: () => string;
  navigation: () => NavigationTimings | undefined;
  observeVisibility: (listener: () => void) => () => void;
}

function safeMilliseconds(value: number): string | undefined {
  return Number.isFinite(value) && value >= 0 && value <= 9999999 ? String(Math.round(value)) : undefined;
}

export function createStartupPerformance(
  track: (event: 'startup_phase_performance', props: Record<string, string>) => void,
  environment: StartupTimingEnvironment = {
    now: () => performance.now(),
    visibility: () => document.visibilityState,
    // SAFETY: The navigation entry type is defined by the browser Performance API.
    navigation: () => performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined,
    observeVisibility: listener => {
      document.addEventListener('visibilitychange', listener);
      return () => document.removeEventListener('visibilitychange', listener);
    },
  },
) {
  const observationStart = environment.now();
  let observedHidden = environment.visibility() === 'hidden';
  const dispose = environment.observeVisibility(() => { observedHidden ||= environment.visibility() === 'hidden'; });
  function context() {
    const start = safeMilliseconds(observationStart);
    const props: StartupVisibilityProps = {
      visibility_state: environment.visibility() === 'hidden' ? 'hidden' : 'visible',
      visibility_observed_hidden: String(observedHidden),
    };
    if (start !== undefined) props.visibility_observation_start_ms = start;
    return props;
  }
  function checkpoint() {
    const props: Record<string, string> = {};
    const navigation = environment.navigation();
    if (!navigation) return { ...context(), ...props };
    for (const [key, start, end] of [
      ['dns_ms', navigation.domainLookupStart, navigation.domainLookupEnd],
      ['connect_ms', navigation.connectStart, navigation.connectEnd],
      ['request_ms', navigation.requestStart, navigation.responseStart],
      ['response_ms', navigation.responseStart, navigation.responseEnd],
      ['dom_interactive_ms', 0, navigation.domInteractive],
    ] as const) {
      // Zero end timestamps mean that the browser has not completed this phase.
      const value = end > 0 ? safeMilliseconds(end - start) : undefined;
      if (value !== undefined) props[key] = value;
    }
    return { ...context(), ...props };
  }
  const startedPhases = new Set<StartupPhase>();
  function startPhase(phase: StartupPhase) {
    if (startedPhases.has(phase)) return () => {};
    startedPhases.add(phase);
    const started = environment.now();
    let finished = false;
    return (outcome: StartupOutcome) => {
      if (finished) return;
      finished = true;
      const ended = environment.now();
      const duration = safeMilliseconds(ended - started);
      const elapsed = safeMilliseconds(ended);
      if (duration === undefined || elapsed === undefined) return;
      track('startup_phase_performance', { phase, outcome, duration_ms: duration, elapsed_ms: elapsed, ...context() });
    };
  }
  async function measurePromise<T>(phase: StartupPhase, run: () => Promise<T>): Promise<T> {
    const finish = startPhase(phase);
    try { const result = await run(); finish('completed'); return result; }
    catch (error) { finish('failed'); throw error; }
  }
  return { checkpoint, startPhase, measurePromise, dispose };
}
